/**
 * Chart registry: single source of truth for all Overview charts.
 * Each entry defines metadata, category, drill-through target, and optional method docs.
 * Rendering is handled by Charts.tsx chart components via chartId lookup.
 *
 * This folder ('overview') contains the chart catalogue registry, renderers, and data hook.
 * It is unrelated to the Overview/Cockpit analytics page.
 */

import type { ChartMethodContent, ControlDefinition } from '@/components/visuals';

export type ChartCategory = 'Schedule' | 'Milestones' | 'WBS' | 'Quality' | 'Trends';

export interface ChartDefinition {
  /** Unique slug used in URL params, e.g. "float_distribution" */
  id: string;
  /** Display title (Title Case) */
  title: string;
  /** Short description shown below title (sentence case) */
  description: string;
  /** Category for filtering */
  category: ChartCategory;
  /** Default compact-card height in px */
  compactHeight: number;
  /** Focus-mode height in px */
  focusHeight: number;
  /** Primary drill-through label */
  primaryActionLabel: string;
  /** Secondary drill-through label (optional) */
  secondaryActionLabel?: string;
  /** Whether this chart is shown in the default "Pinned" view */
  pinned: boolean;
  /** Optional methodology content shown in focus mode */
  method?: ChartMethodContent;
  /** Control schema for focus mode (show-count, sort, show-labels, search, etc.). Omit or empty = no control strip. */
  controls?: ControlDefinition[];
  /** Priority for Recommended section (higher = recommended first; default 0) */
  priority?: number;
  /** When true, chart requires snapshot history (≥2 imports); show empty state "Add more imports to see this trend." when not met. */
  requiresSnapshotHistory?: boolean;
}

export const CHART_REGISTRY: ChartDefinition[] = [
  {
    id: 'float_distribution',
    priority: 10,
    title: 'Float Distribution',
    description: 'Activity float by bucket. Click a bucket to drill into Activities.',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: true,
    controls: [{ type: 'show-labels', default: true }],
    method: {
      what: 'Histogram of activity total float grouped into day-range buckets (<0, 0, 1–7, 8–14, 15–30, 31–44, >44).',
      calculation: [
        'Each activity\'s total_float_hr_cnt is divided by 8 to approximate days.',
        'Activities are binned into predefined buckets.',
      ],
      interpretation: [
        'Bars on the left (< 0 and 0) represent critical and negative-float activities.',
        'A distribution skewed left indicates schedule compression.',
      ],
      drillthrough: [
        'Click <0 bucket → Activities filtered to negative float.',
        'Click 0 bucket → Activities filtered to critical.',
        'Click other buckets → Activities filtered by float range (float_min / float_max).',
      ],
      inputs: ['Activity total_float_hr_cnt from XER task data.'],
      thresholds: ['Float buckets: <0, 0, 1–7, 8–14, 15–30, 31–44, >44 days.'],
      caveats: ['Float is derived from total_float_hr_cnt / 8; calendar may vary.'],
    },
  },
  {
    id: 'criticality_breakdown',
    priority: 9,
    title: 'Criticality Breakdown',
    description: 'Critical, near-critical, and monitor activity counts.',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Health',
    pinned: true,
    controls: [{ type: 'show-labels', default: true }],
    method: {
      what: 'Donut chart showing how many non-completed activities fall into Critical (float ≤ 0d), Near-critical (0–5d), and Monitor (5–10d) bands.',
      calculation: [
        'Float days = total_float_hr_cnt / 8.',
        'Critical: float ≤ 0 days and not completed.',
        'Near-critical: 0 < float ≤ 5 days and not completed.',
        'Monitor: 5 < float ≤ 10 days and not completed.',
      ],
      interpretation: [
        'Large "Critical" slice = most activities have zero or negative float.',
        'Compare across snapshots to detect schedule deterioration.',
      ],
      drillthrough: [
        'Click Critical → Activities filtered to critical=true.',
        'Click Near-critical or Monitor → Analytics Critical Path page.',
      ],
      inputs: ['Activity total_float_hr_cnt, status/completion from XER task data.'],
      thresholds: ['Critical: float ≤ 0d. Near-critical: 0–5d. Monitor: 5–10d.'],
      caveats: ['Completed activities are excluded from all bands.'],
    },
  },
  {
    id: 'milestone_variance',
    priority: 8,
    title: 'Top Milestone Variance',
    description: 'Worst variance first. Click to open Milestones.',
    category: 'Milestones',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Milestones',
    pinned: true,
    controls: [
      { type: 'show-count', options: [25, 50, 100, 200, 'all'], default: 'all' },
      { type: 'sort', default: 'worst-first' },
      { type: 'include-negatives', default: true },
      { type: 'show-labels', default: true },
      { type: 'search', placeholder: 'Search…' },
    ],
    method: {
      what: 'Horizontal bar chart of milestones ranked by variance in days between current forecast and baseline/target finish date.',
      calculation: [
        'Baseline date = target_end_date from XER task data (user-entered baseline).',
        'Current date = early_end_date (forecast finish).',
        'Variance = current − baseline in calendar days. Positive = late, negative = early.',
        'Milestones without a target_end_date are excluded (empty state shown if none have baselines).',
      ],
      interpretation: [
        'Positive (red) bars = milestone is forecasting later than baseline.',
        'Negative (green) bars = milestone is ahead of baseline.',
        'Focus on the most-positive bars for risk.',
      ],
      drillthrough: [
        'Click any bar or list row → Analytics Milestones page.',
      ],
      inputs: ['Activity target_end_date (baseline), early_end_date (forecast), milestone flag from XER task data.'],
      thresholds: ['Variance = forecast finish − target_end_date in calendar days.'],
      caveats: ['Milestones without a baseline date are excluded.'],
    },
  },
  {
    id: 'wbs_hotspots',
    priority: 7,
    title: 'WBS Hotspots',
    description: 'Risk score by WBS. Higher = worse (negative float, near-critical %, overdue). Negative = safe.',
    category: 'WBS',
    compactHeight: 280,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: true,
    controls: [
      { type: 'show-count', options: [25, 50, 100, 200, 'all'], default: 50 },
      { type: 'sort', default: 'worst-first' },
      { type: 'include-negatives', default: true },
      { type: 'show-labels', default: true },
      { type: 'search', placeholder: 'Search…' },
    ],
    method: {
      what: 'Horizontal bar chart ranking WBS nodes by a composite risk score. Higher score = more risk concentration.',
      calculation: [
        'Activities are grouped by wbs_id.',
        'Per WBS, compute: minFloatDays = min(total_float_hr_cnt / 8) across all activities.',
        'criticalNearCriticalPct = % of non-completed activities with float ≤ 5 days.',
        'overdueCount = count of non-completed activities whose early_end_date < today.',
        'hotspotScore = −minFloatDays × 2 + criticalNearCriticalPct × 0.5 + overdueCount × 3.',
      ],
      interpretation: [
        'Positive score → at-risk WBS (has negative float, many near-critical activities, or overdue tasks).',
        'Negative score → safe WBS (all activities have large positive float). e.g. −800 means ~400 days of minimum float.',
        'Score magnitude is relative — use for ranking, not as an absolute KPI.',
      ],
      drillthrough: [
        'Click a bar or list row → Activities page filtered to that WBS.',
      ],
      inputs: [
        'Activity total_float_hr_cnt, early_end_date, status, wbs_id, wbs_name from XER task data.',
        'Excludes completed activities from criticality and overdue counts.',
      ],
      thresholds: ['Near-critical: float ≤ 5d. Overdue: early_end_date < data date.'],
      caveats: ['Score is composite; use for ranking only, not absolute KPI.'],
    },
  },
  {
    id: 'quality_issues',
    priority: 6,
    title: 'Quality Issues',
    description: 'Open ends, lags, constraints. Click for Quality diagnostics.',
    category: 'Quality',
    compactHeight: 280,
    focusHeight: 560,
    primaryActionLabel: 'View Quality',
    pinned: true,
    controls: [{ type: 'show-labels', default: true }],
    method: {
      what: 'Vertical bar chart showing counts of schedule quality problems: missing predecessors, missing successors, excessive lags, hard constraints.',
      calculation: [
        'Missing predecessors: non-LOE, non-WBS, non-milestone activities with no incoming relationship (not present as successor_id in TASKPRED).',
        'Missing successors: same eligible activities with no outgoing relationship (not present as predecessor_id in TASKPRED).',
        'Excessive lags: relationships where |lag_hours| > 40 (≈ 5 working days).',
        'Hard constraints: activities where primary_constraint_type is set and not default (CS_MEO or CS_ALAP).',
      ],
      interpretation: [
        'High missing-predecessor count = open-ended logic (activities not driven by schedule).',
        'High missing-successor count = dangling ends (activities that don\'t drive anything).',
        'Excessive lags may indicate padding or incorrect logic.',
      ],
      drillthrough: [
        'Click any bar → Analytics Quality page.',
      ],
      inputs: [
        'TASKPRED (predecessor_id, successor_id, lag_hours) for relationships.',
        'TASK (task_type, primary_constraint_type) for activities.',
      ],
      thresholds: [
        'High float %: pass ≤15%, warn 16–30%, fail >30%. Long duration: >44d. Excessive lag: >40h.',
        'Missing relations: pass=0, warn 1–3, fail >3. Constraints: pass ≤10.',
      ],
      caveats: ['LOE, WBS summary, and milestone activities excluded from missing-relation counts.'],
    },
  },
  {
    id: 's_curve_activities',
    priority: 5,
    title: 'Planned vs Actual Activities',
    description: 'Cumulative count of activities by planned and actual completion date.',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: false,
    controls: [{ type: 'show-baseline', default: false }], // No show-labels; S-curves too dense. Show-baseline toggles baseline series when present.
    method: {
      what: 'Cumulative count of activities over time: planned (by target_end_date) and actual (by act_end_date for completed).',
      calculation: [
        'Planned: each task contributes to the bucket of its target_end_date; cumulative sum over dates.',
        'Actual: completed tasks (status TK_Complete) contribute to the bucket of act_end_date; cumulative sum.',
      ],
      interpretation: ['Planned curve shows intended completion pace; actual shows achieved pace.', 'Gap between curves indicates schedule slip or lead.'],
      drillthrough: ['Click View Activities to open the activity list.'],
      inputs: ['TASK: task_type, status_code, target_end_date, act_end_date (XER/Parquet).'],
      thresholds: ['Task types TT_Task, TT_FinMile, TT_Mile; LOE/WBS excluded.'],
      caveats: ['Uses current snapshot only; target dates as planned baseline.'],
    },
  },
  {
    id: 's_curve_duration',
    priority: 5,
    title: 'Planned vs Earned Duration',
    description: 'Cumulative planned and earned duration (hours) over time.',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: false,
    controls: [{ type: 'show-baseline', default: false }], // No show-labels; S-curves too dense. Show-baseline toggles baseline series when present.
    method: {
      what: 'Cumulative planned duration (hours) and earned duration (hours) over time. Earned = target_drtn_hr_cnt × phys_complete_pct/100.',
      calculation: [
        'Planned: each task contributes target_drtn_hr_cnt to the bucket of its target_end_date; cumulative.',
        'Earned: each task contributes target_drtn_hr_cnt × (phys_complete_pct/100) to act_end_date or target_end_date; cumulative.',
      ],
      interpretation: ['Earned schedule: earned curve above planned = ahead; below = behind.'],
      drillthrough: ['Click View Activities to open activities with schedule data.'],
      inputs: ['TASK: target_drtn_hr_cnt, phys_complete_pct, target_end_date, act_end_date.'],
      caveats: ['Uses current snapshot; target dates as planned baseline.'],
    },
  },
  {
    id: 'units_per_day_histogram',
    priority: 5,
    title: 'Planned Units per Day',
    description: 'Time-phased bars: total planned units per calendar day (even spread over each activity’s duration).',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: false,
    controls: [
      { type: 'schedule-date-basis', default: 'baseline' },
      { type: 'include-milestones', default: false },
      { type: 'units-spread-basis', default: 'working' },
      { type: 'show-labels', default: false },
    ],
    method: {
      what: 'Project-controls time-phased workload (not a statistical histogram). Each included activity’s planned units are spread evenly across **working days** on that activity’s P6 calendar (parsed from `clndr_data`), or across inclusive **calendar days** when you choose Calendar days.',
      calculation: [
        'Dates: Target dates = target start/finish from schedule data (not a formal project baseline snapshot). Early/current = early start/finish with fallbacks where needed.',
        'By default only normal task rows (e.g. TT_Task) are included; TT_Mile / TT_FinMile are excluded—use Include milestone units when needed.',
        'Working-days mode: denominators use activity `clndr_id` mapped through CALENDAR rows; project default calendar when blank. Calendar-days mode mirrors the legacy MVP (every calendar day inclusive).',
        'Invalid date order places all units on the start date. Focus mode shows method counters and peak contributors.',
      ],
      interpretation: ['Peaks show heaviest combined planned workload; use Peak contributors to explain spikes.'],
      drillthrough: ['Click View Activities to open the activity list.'],
      inputs: ['TASK: target_work_qty, task_type, clndr_id, dates; CALENDAR.clndr_data.', 'Snapshots with schedule analytics must include TASK + CALENDAR to enable server working-day series.'],
      caveats: ['Working-day parity with Primavera depends on calendar parse coverage; extend `CALENDAR_WORKING_DAY_SPREAD` env to disable server engine.'],
    },
  },
  {
    id: 'planned_units_s_curve',
    priority: 5,
    title: 'Planned Units S-Curve',
    description: 'Cumulative planned units over the schedule from the same time-phased spread as Planned Units per Day.',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: false,
    controls: [
      { type: 'schedule-date-basis', default: 'baseline' },
      { type: 'include-milestones', default: false },
      { type: 'units-spread-basis', default: 'working' },
      { type: 'show-labels', default: false },
    ],
    method: {
      what: 'Cumulative sum of daily planned units along the project timeline—the integral of the time-phased units/day profile.',
      calculation: [
        'Same inclusion rules and Units spread setting as Planned Units per Day (working vs calendar days).',
        'Final cumulative should match total planned units for included rows and the histogram footer check.',
      ],
      interpretation: ['Uses the same spread basis as the paired histogram.', 'Toggle Target vs Early/current dates or milestone inclusion to stay aligned.'],
      drillthrough: ['Click View Activities to open the activity list.'],
      inputs: ['Same as Planned Units per Day.'],
      caveats: ['Planned only (no earned curve); see Planned vs Earned Units for earned progress.'],
    },
  },
  {
    id: 's_curve_units',
    priority: 5,
    title: 'Planned vs Earned Units',
    description: 'Cumulative work units over the project (planned finish vs earned progress).',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Activities',
    pinned: false,
    controls: [{ type: 'show-baseline', default: false }],
    method: {
      what: 'Cumulative target_work_qty over time vs earned units (target_work_qty × phys_complete_pct / 100).',
      calculation: [
        'Planned curve: each activity’s full target_work_qty is credited on its target_end_date; running sum by date.',
        'Earned curve: each activity credits target_work_qty × (phys_complete_pct / 100) on act_end_date when present, otherwise target_end_date; running sum.',
      ],
      interpretation: ['Earned above planned = ahead on a units basis; below = behind.', 'Shape is the units S-curve for the project.'],
      drillthrough: ['Click View Activities to open activities with work quantity data.'],
      inputs: ['TASK: target_work_qty, phys_complete_pct, target_end_date, act_end_date, status.'],
      caveats: ['Uses current snapshot target dates as the planned baseline.', 'Activities with zero target_work_qty are excluded.'],
    },
  },
  {
    id: 's_curve_wbs',
    priority: 5,
    title: 'WBS Progress',
    description: 'Earned vs planned progress by top-level WBS over time.',
    category: 'Schedule',
    compactHeight: 280,
    focusHeight: 560,
    primaryActionLabel: 'View WBS',
    pinned: false,
    controls: [{ type: 'show-baseline', default: false }], // No show-labels; S-curves too dense. Show-baseline toggles baseline series when present.
    method: {
      what: 'Per top-level WBS: cumulative planned and earned hours over time. Earned = sum(target_drtn_hr_cnt × phys_complete_pct/100) under that WBS.',
      calculation: [
        'Tasks are rolled up to root WBS via parent_wbs_id hierarchy.',
        'Planned = sum of target_drtn_hr_cnt by date; earned = sum of earned hours by date; both cumulative.',
      ],
      interpretation: ['Compare earned vs planned by WBS to see which phases are ahead or behind.'],
      drillthrough: ['Click View WBS to open activities filtered by WBS.'],
      inputs: ['TASK + PROJWBS: target_drtn_hr_cnt, phys_complete_pct, target_end_date, wbs_id, parent_wbs_id.'],
      caveats: ['Uses current snapshot; target dates as planned baseline.'],
    },
  },
  {
    id: 'milestone_on_time',
    priority: 5,
    title: 'Milestone On-Time Performance',
    description: 'Percentage of milestones completed on or before target date by period.',
    category: 'Milestones',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Milestones',
    pinned: false,
    controls: [], // No show-labels control; labels off by default for legend clarity.
    method: {
      what: 'By period (month): percentage of completed milestones that finished on or before their target_end_date.',
      calculation: [
        'Milestones: task_type TT_FinMile, TT_Mile. On-time = act_end_date ≤ target_end_date.',
        'Period = month of completion; on_time_pct = 100 × on_time_count / total completed in period.',
      ],
      interpretation: ['High % = most milestones delivered on time in that period; low % indicates slip.'],
      drillthrough: ['Click View Milestones to open the milestones page.'],
      inputs: ['TASK: task_type, target_end_date, act_end_date, status_code.'],
      caveats: ['Uses current snapshot; target dates as baseline. Only completed milestones counted.'],
    },
  },
  {
    id: 'resource_demand_capacity',
    priority: 5,
    title: 'Resource Demand vs Capacity',
    description: 'Time-bucketed resource demand and capacity (when available).',
    category: 'Schedule',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Resources',
    pinned: false,
    controls: [
      { type: 'show-labels', default: true },
      { type: 'show-count', options: [10, 25, 50, 'all'], default: 'all' }, // Limit buckets/snapshots shown.
    ],
    method: {
      what: 'Monthly buckets: sum of target_qty (hours) from TASKRSRC attributed to target_start_date. Capacity (when available) from RSRCRATE.',
      calculation: [
        'Demand: TASKRSRC.target_qty grouped by month of target_start_date.',
        'Capacity: requires RSRCRATE.max_qty_per_hr; currently stubbed when not in lake.',
      ],
      interpretation: ['Demand above capacity = over-allocation risk.'],
      drillthrough: ['Click View Resources for resource views.'],
      inputs: ['TASKRSRC: target_qty, target_start_date; RSRC/RSRCRATE for capacity.'],
      caveats: ['Capacity may show as unavailable until RSRCRATE is ingested.'],
    },
  },
  {
    id: 'critical_path_length',
    priority: 4,
    title: 'Critical Path Length Over Time',
    description: 'Remaining critical path duration (days) per snapshot.',
    category: 'Trends',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Critical Path',
    pinned: false,
    requiresSnapshotHistory: true,
    controls: [
      { type: 'show-labels', default: true },
      { type: 'show-count', options: [10, 25, 50, 'all'], default: 'all' }, // Limit snapshots (points) shown.
    ],
    method: {
      what: 'For each snapshot (import): sum of remain_drtn_hr_cnt/8 for critical-path activities (driving_path_flag = Y or total_float = 0), non-completed.',
      calculation: ['Per snapshot: register views for that import, then sum remain_drtn_hr_cnt/8 for CP activities.'],
      interpretation: ['X-axis = snapshot upload date. Rising trend = CP lengthening; falling = shortening.'],
      drillthrough: ['Click View Critical Path for critical path page.'],
      inputs: ['TASK: driving_path_flag, total_float_hr_cnt, remain_drtn_hr_cnt, status_code per snapshot.'],
      caveats: ['Requires ≥2 snapshots. Add more imports to see trend.'],
    },
  },
  {
    id: 'float_trend',
    priority: 4,
    title: 'Total Float Trend',
    description: 'Average and minimum float (days) per snapshot over time.',
    category: 'Trends',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Float',
    pinned: false,
    requiresSnapshotHistory: true,
    controls: [
      { type: 'show-labels', default: true },
      { type: 'show-count', options: [10, 25, 50, 'all'], default: 'all' }, // Limit snapshots shown.
    ],
    method: {
      what: 'Per snapshot: average and minimum of total_float_hr_cnt/8 (days) for non-completed, non-LOE/WBS tasks.',
      calculation: ['Per snapshot: AVG and MIN(total_float_hr_cnt)/8 over eligible tasks.'],
      interpretation: ['Declining min float = schedule tightening; negative min = critical/negative float.'],
      drillthrough: ['Click View Float for float/health views.'],
      inputs: ['TASK: total_float_hr_cnt, status_code, task_type per snapshot.'],
      caveats: ['Requires ≥2 snapshots.'],
    },
  },
  {
    id: 'near_critical_trend',
    priority: 4,
    title: 'Near-Critical Activities Trend',
    description: 'Count of activities with float within threshold per snapshot.',
    category: 'Trends',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Near-Critical',
    pinned: false,
    requiresSnapshotHistory: true,
    controls: [
      { type: 'show-labels', default: true },
      { type: 'show-count', options: [10, 25, 50, 'all'], default: 'all' }, // Limit snapshots shown.
    ],
    method: {
      what: 'Per snapshot: count of activities where total_float_hr_cnt > 0 and total_float_hr_cnt ≤ threshold_hr (e.g. 80 hr = 10 days).',
      calculation: ['Threshold configurable (default 10 days). Count non-completed tasks in (0, threshold] float.'],
      interpretation: ['Rising count = more activities becoming near-critical over time.'],
      drillthrough: ['Click View Near-Critical for analytics.'],
      inputs: ['TASK: total_float_hr_cnt, status_code per snapshot.'],
      thresholds: ['Default 10 days (80 hr); configurable via API.'],
      caveats: ['Requires ≥2 snapshots.'],
    },
  },
  {
    id: 'logic_integrity_trend',
    priority: 4,
    title: 'Logic Integrity Trend',
    description: 'Missing predecessors/successors, out-of-sequence, constrained counts per snapshot.',
    category: 'Trends',
    compactHeight: 280,
    focusHeight: 560,
    primaryActionLabel: 'View Quality',
    pinned: false,
    requiresSnapshotHistory: true,
    controls: [
      { type: 'show-labels', default: true },
      { type: 'show-count', options: [10, 25, 50, 'all'], default: 'all' }, // Limit snapshots shown.
    ],
    method: {
      what: 'Per snapshot: counts of missing predecessors, missing successors, out-of-sequence, and hard-constrained activities.',
      calculation: [
        'Missing pred/succ: tasks not appearing in TASKPRED as successor/predecessor.',
        'Constrained: cstr_type set and not default. OOS stubbed where not trivial.',
      ],
      interpretation: ['Rising counts = logic quality degrading over imports; declining = improving.'],
      drillthrough: ['Click View Quality for quality page.'],
      inputs: ['TASK + TASKPRED: task_id, pred_task_id, cstr_type per snapshot.'],
      caveats: ['Requires ≥2 snapshots.'],
    },
  },
  {
    id: 'phase_progress_burnup',
    priority: 4,
    title: 'Phase Progress Burn-up',
    description: 'Earned vs planned % complete by top-level WBS per snapshot.',
    category: 'Trends',
    compactHeight: 280,
    focusHeight: 560,
    primaryActionLabel: 'View WBS',
    pinned: false,
    requiresSnapshotHistory: true,
    controls: [
      { type: 'show-labels', default: true },
      { type: 'show-count', options: [10, 25, 50, 'all'], default: 'all' }, // Limit snapshots shown.
    ],
    method: {
      what: 'Per snapshot: for each top-level WBS, earned % = 100 × sum(earned duration) / sum(planned duration). Earned = target_drtn_hr_cnt × phys_complete_pct/100.',
      calculation: ['Per snapshot: roll up tasks to root WBS; sum planned and earned hours; pct_complete = 100×earned/planned.'],
      interpretation: ['Each line = one WBS phase. Burn-up over time shows phase completion across imports.'],
      drillthrough: ['Click View WBS for WBS/activity views.'],
      inputs: ['TASK + PROJWBS: target_drtn_hr_cnt, phys_complete_pct, wbs_id, parent_wbs_id per snapshot.'],
      caveats: ['Requires ≥2 snapshots.'],
    },
  },
  {
    id: 'trends_snapshots',
    priority: 1,
    title: 'Trends Over Snapshots',
    description: 'Schedule metrics over time (requires multiple imports).',
    category: 'Trends',
    compactHeight: 260,
    focusHeight: 560,
    primaryActionLabel: 'View Project Details',
    pinned: true,
    requiresSnapshotHistory: true,
    controls: [], // No controls; renders only an empty state, not a chart.
  },
];

export const ALL_CATEGORIES: ChartCategory[] = ['Schedule', 'Milestones', 'WBS', 'Quality', 'Trends'];

/** Lookup a chart definition by id */
export function getChartById(id: string): ChartDefinition | undefined {
  return CHART_REGISTRY.find((c) => c.id === id);
}
